<template>
  <div v-if="cashAccountsStore.entitledCashAccountsForCurrentContext.length > 1">
    <div class="pb-2 pt-4 dark:text-yapgray-200">{{ $t('webapp.nav.otherAccounts') }}</div>
    <ul class="mb-12 flex flex-col rounded-lg bg-white p-1 dark:bg-yapdarkgray-800">
      <li
        v-for="cashAccount in cashAccountsStore.entitledCashAccountsForCurrentContext"
        :key="cashAccount._links.self.uri"
        class="cursor-pointer p-2"
        :class="{
          'rounded-lg bg-primary-50 dark:bg-primary-dark-900':
            cashAccount._links.self.uri === getCurrentCashAccount?._links.self.uri,
          'opacity-60': cashAccount.status !== 'active'
        }"
        @click="loadCashAccountDetails(cashAccount)"
      >
        <AccountEntry
          :name="cashAccount.accountName ?? 'n/a'"
          :is-clickable="true"
          :status="cashAccount.status"
          v-if="cashAccount.entitlements && !cashAccount.entitlements.map((e) => e.user.uid).includes(userUid)"
        />
        <AccountEntry
          :name="cashAccount.accountName ?? 'n/a'"
          :currency="cashAccount.currency"
          :amount="cashAccount.totalValuation || cashAccount.totalValuation"
          :is-clickable="true"
          :status="cashAccount.status"
          v-else
        />
        <div v-if="cashAccount.status !== 'active'" class="text-xs text-red-500">
          {{ t(`app.partnership.account.status.${cashAccount.status}`) }}
        </div>
      </li>
      <router-link
        v-if="!contextSwitchStore.isPrivateContext && !isBillingAccountCancelling"
        :to="{ name: RouteNames.CashAccountCreate }"
        @click="$emit('on-navigate')"
      >
        <li class="cursor-pointer p-2">
          <div
            class="group flex grow gap-2 overflow-hidden text-yapgray-700 hover:text-primary-500 dark:text-yapgray-200 dark:hover:text-primary-dark-300"
          >
            <div
              class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-yapgray-300 dark:bg-yapdarkgray-600"
            >
              <YapIcon name="add-outline" class="w-6 text-yapgray-800 group-hover:text-inherit dark:text-yapgray-200" />
            </div>
            <div
              class="text-md flex shrink grow items-center overflow-hidden text-ellipsis whitespace-nowrap font-semibold leading-6"
            >
              {{ t(`app.partnership.cashAccounts.add_new_account`) }}
            </div>
          </div>
        </li>
      </router-link>
    </ul>
  </div>
  <div
    v-else-if="
      !contextSwitchStore.isPrivateContext &&
      !userStore.isPotatoOnly &&
      !partnershipsStore.isRequestorBootstrapperForCurrentPartnership &&
      !isBillingAccountCancelling
    "
  >
    <div class="pb-2 pt-4 dark:text-yapgray-200">{{ t('webapp.nav.otherAccounts') }}</div>
    <ul class="mb-12 flex flex-col rounded-lg bg-white p-1 dark:bg-yapdarkgray-800">
      <router-link :to="{ name: RouteNames.CashAccountCreate }" @click="$emit('on-navigate')">
        <li class="cursor-pointer p-2">
          <div
            class="group flex grow gap-2 overflow-hidden text-yapgray-700 hover:text-primary-500 dark:text-yapgray-200 dark:hover:text-primary-dark-300"
          >
            <div
              class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-yapgray-300 dark:bg-yapdarkgray-600"
            >
              <YapIcon
                name="add-outline"
                class="size-6 text-yapgray-800 group-hover:text-inherit dark:text-yapgray-200"
              />
            </div>
            <div
              class="text-md flex shrink grow items-center overflow-hidden text-ellipsis whitespace-nowrap font-semibold leading-6"
            >
              {{ t(`app.partnership.cashAccounts.add_new_account`) }}
            </div>
          </div>
        </li>
      </router-link>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { RouteNames } from '@/router/routes';
import { useI18n } from '@/plugins/i18n';
import { useUserStore } from '@/store/userStore';
import { useRoute } from 'vue-router';
import { CashAccount } from '@/types/cashAccount';
import { createResourceUri, getUidFromSelfLink } from '@/utils/links';
import { useCashAccountsStore } from '@/store/cashAccountsStore';
import { useEventStreamStore } from '@/store/eventStreamStore';
import { useContextSwitchStore } from '@/store/contextSwitchStore';
import AccountEntry from '@/components/sideMenu/AccountEntry.vue';
import YapIcon from '@/components/YapIcon.vue';
import { PartnershipURI } from '@/types/partnership';
import { debouncedWatch } from '@vueuse/core';
import { usePartnershipsStore } from '@/store/partnershipsStore';
import { useEntitlementsStore } from '@/store/entitlementsStore';

const { t } = useI18n();
const userStore = useUserStore();
const route = useRoute();
const cashAccountsStore = useCashAccountsStore();
const eventStreamStore = useEventStreamStore();
const contextSwitchStore = useContextSwitchStore();
const partnershipsStore = usePartnershipsStore();
const entitlementsStore = useEntitlementsStore();

const { getCurrentCashAccount } = storeToRefs(cashAccountsStore);

const userUid = computed(() => userStore.uid);
const emit = defineEmits(['on-navigate']);

async function loadCashAccountDetails(cashAccount: CashAccount) {
  if (cashAccount) {
    await cashAccountsStore.getCashAccountDetails(cashAccount._links.self);
    cashAccountsStore.selectCashAccount(getUidFromSelfLink(cashAccount._links.self));
  }
}

function checkForCancellingCashAccounts() {
  const allCashAccounts = cashAccountsStore.cashAccountsForCurrentContext;
  return allCashAccounts.some((ca) => ca.status === 'cancelling' && ca.billingAccount);
}

const isBillingAccountCancelling = computed(() => checkForCancellingCashAccounts());

onMounted(async () => {
  checkForCancellingCashAccounts();
});

debouncedWatch(
  () => eventStreamStore.stale_cash_accounts,
  async (newVal) => {
    checkForCancellingCashAccounts();
    if (newVal && RouteNames.CashAccountCreate != route.name) {
      cashAccountsStore.loadCashAccounts(
        createResourceUri(PartnershipURI.Base, {
          uid: contextSwitchStore.currentContextUid,
          resource: PartnershipURI.CashAccounts
        }),
        true
      );

      entitlementsStore.loadEntitlements(contextSwitchStore.currentContextUid);
    }
  },
  { deep: true, debounce: 700 }
);
</script>
